// Duplicated from service-desk's projectFilterUtils + ProjectSelectColumn helpers.
// Code duplication is accepted between dashboard-ui and service-desk (sealed UMD boundary).
import { ApolloClient } from '@apollo/client';
import { SearchQARuleEntries } from './queries';
import { Collection } from './types';

// ─── Constants ────────────────────────────────────────────────────────────────

export const QA_RULE_LEVEL_CQA_SAMPLES = '3';
export const LABEL_INDIVIDUAL = 'individual';

export const multiUserProjectDefault = [
  { value: 'IN_CQA', key: 'qa_status', operation: 'eq' },
  { value: 'CQA', key: 'case_type', operation: 'eq' },
];

export const multiUserProjectFiltersCoder = [
  ...multiUserProjectDefault,
  { value: 'MY_CASES', key: 'assignee', operation: 'eq' },
];

export const singleUserProjectFiltersCoder = [
  { value: 'IN_CQA', key: 'qa_status', operation: 'eq' },
  {
    filters: [
      { key: 'assignee', operation: 'in', value: 'MY_GROUPS' },
      { key: 'assignee', operation: 'in', value: 'MY_GROUP_CODERS' },
    ],
    combinator: 'OR',
  },
  { value: 'CQA', key: 'case_type', operation: 'eq' },
];

// ─── Role helpers ─────────────────────────────────────────────────────────────

export function getUserRolesFromActiveContext(userContext: any): string[] {
  if (!userContext?.activeContext || !userContext?.user?.roles) return [];
  const tenantId = userContext.activeContext.tenantId;
  const matchingRole = userContext.user.roles.find(
    (role: any) => role.tenantId === tenantId,
  );
  return matchingRole ? matchingRole.roles.map((r: any) => r.roleName) : [];
}

// ─── Project item construction ────────────────────────────────────────────────

export function createProjectItemFromQaRule(
  qaRule: any,
  activeContextRoles: string[],
  workerId: string,
): Collection {
  const collectionIdFilter = {
    key: 'qa_collection_id',
    operation: 'eq',
    value: qaRule.data.collection_id,
  };

  const isManagerOrAuditor =
    activeContextRoles.includes('cqa_manager') ||
    qaRule.data.project_configuration.auditors?.includes(workerId);
  const multiUserFilter = isManagerOrAuditor
    ? multiUserProjectDefault
    : multiUserProjectFiltersCoder;

  const audience = qaRule.data.project_configuration.audience;
  const audienceConfigured = audience?.distributionMethod;
  const individualDistribution =
    audienceConfigured && audience.distributionMethod === LABEL_INDIVIDUAL;

  const baseFilters =
    qaRule.data.assignee_ids == null
      ? [...singleUserProjectFiltersCoder, collectionIdFilter]
      : [...multiUserFilter, collectionIdFilter];

  const audienceFilters = individualDistribution
    ? [...multiUserFilter, collectionIdFilter]
    : [...singleUserProjectFiltersCoder, collectionIdFilter];

  return {
    name: qaRule.data.name,
    collection_id: qaRule.data.collection_id,
    availability: qaRule.data.availability,
    filters: audienceConfigured ? audienceFilters : baseFilters,
    assignee_ids: qaRule.data.assignee_ids,
    rubricVersionId: qaRule.data.project_configuration?.rubricVersionId,
    project_configuration: qaRule.data.project_configuration,
  };
}

// ─── Project visibility filter ────────────────────────────────────────────────

export function filterProject(
  qaRule: any,
  activeContextRoles: string[],
  userRoles: any[],
  workerId: string,
): boolean {
  if (activeContextRoles.includes('cqa_manager')) return true;

  const audience = qaRule.data?.project_configuration?.audience;

  if (audience?.distributionMethod === LABEL_INDIVIDUAL) {
    return !!audience?.individuals?.some((c: any) => c?.value === workerId);
  }

  const auditors = qaRule.data.project_configuration.auditors;
  if (auditors?.length > 0 && auditors.includes(workerId)) return true;

  if (qaRule.data.assignee_ids?.includes(workerId)) return true;

  const groupValue = qaRule.data?.group_id?.value;
  if (groupValue && userRoles?.some((role: any) => role.group_id === groupValue))
    return true;

  const visibility: any[] = qaRule.data.project_configuration?.audience?.groupVisibility ?? [];
  if (visibility.length > 0) {
    const groupVisibilityIds = new Set(visibility.map((g: any) => g.value));
    const userGroupIds = userRoles.map((r: any) => r.group_id);
    if (userGroupIds.some(id => groupVisibilityIds.has(id))) return true;
  }

  return false;
}

// ─── QA rule fetch ────────────────────────────────────────────────────────────

export async function fetchQaRules(
  apolloClient: ApolloClient<any>,
  serviceLine: string,
): Promise<any[]> {
  const requestBody = {
    combinator: 'AND',
    filters: [
      { key: 'level', operation: 'eq', value: QA_RULE_LEVEL_CQA_SAMPLES },
      { key: 'service', operation: 'eq', value: serviceLine },
    ],
    level: parseInt(QA_RULE_LEVEL_CQA_SAMPLES),
    service: serviceLine,
  };

  try {
    const response = await apolloClient.mutate({
      mutation: SearchQARuleEntries,
      variables: { request: requestBody },
      fetchPolicy: 'no-cache',
    });
    return response.data?.SearchQarule?.data ?? [];
  } catch (err) {
    console.error('Error fetching QA rules:', err);
    return [];
  }
}
